import React from 'react';
import { Link } from 'react-router-dom';
import { TreePine, Mountain, Leaf, Shovel, HardHat, Calculator, FileText } from 'lucide-react';
import { trackInternalLink } from '@/lib/analytics';

type MaterialKey = 'grass' | 'rock' | 'mulch' | 'soil' | 'sand' | 'products' | 'quote';

interface RelatedMaterialsLinksProps {
  language: 'en' | 'es';
  /** Material keys to exclude (typically the current page). */
  exclude?: MaterialKey[];
  title?: string;
}

const LABELS: Record<'en' | 'es', Record<MaterialKey, string>> = {
  en: {
    grass: 'Grass & Sod',
    rock: 'Decorative Rock',
    mulch: 'Organic Mulch',
    soil: 'Topsoil & Soil',
    sand: 'Sand',
    products: 'All Products',
    quote: 'Free Quote',
  },
  es: {
    grass: 'Pasto y Césped',
    rock: 'Roca Decorativa',
    mulch: 'Mulch Orgánico',
    soil: 'Tierra y Topsoil',
    sand: 'Arena',
    products: 'Todos los Productos',
    quote: 'Cotización Gratis',
  },
};

const HREFS: Record<'en' | 'es', Record<MaterialKey, string>> = {
  en: {
    grass: '/en/grass',
    rock: '/en/rock',
    mulch: '/en/mulch',
    soil: '/en/soil',
    sand: '/en/sand',
    products: '/en/products',
    quote: '/en/quote',
  },
  es: {
    grass: '/es/pasto',
    rock: '/es/roca',
    mulch: '/es/mulch',
    soil: '/es/tierra',
    sand: '/es/arena',
    products: '/es/productos',
    quote: '/es/cotizar',
  },
};

const ICONS: Record<MaterialKey, React.ElementType> = {
  grass: TreePine,
  rock: Mountain,
  mulch: Leaf,
  soil: Shovel,
  sand: HardHat,
  products: FileText,
  quote: Calculator,
};

const RelatedMaterialsLinks: React.FC<RelatedMaterialsLinksProps> = ({
  language,
  exclude = [],
  title,
}) => {
  const order: MaterialKey[] = ['grass', 'rock', 'mulch', 'soil', 'sand', 'products', 'quote'];
  const items = order.filter((k) => !exclude.includes(k));
  const defaultTitle = language === 'es'
    ? 'Explora otros materiales y recursos'
    : 'Explore other materials and resources';

  return (
    <section className="py-12 bg-background" aria-labelledby="related-links-heading">
      <div className="container mx-auto px-4">
        <h2 id="related-links-heading" className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
          {title || defaultTitle}
        </h2>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {items.map((key) => {
            const Icon = ICONS[key];
            return (
              <Link
                key={key}
                to={HREFS[language][key]}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card hover:border-primary hover:shadow-md transition-all text-center"
                data-event-name="internal_link_click"
                data-source={`related-${key}`}
                onClick={() => trackInternalLink(`related-${key}`, HREFS[language][key])}
              >
                <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
                <span className="text-sm font-medium text-foreground">
                  {LABELS[language][key]}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedMaterialsLinks;
